import { baseHelpdeskApi } from "@/config/http-helpdesk";
import {
  CreateHelpdeskTicket,
  HelpdeskAttachment,
  HelpdeskMessage,
  HelpdeskPage,
  HelpdeskTicketDetails,
  HelpdeskTicketSummary,
  TicketCategory,
  TicketPriority,
  TicketStatus,
} from "@/types/helpdesk/Helpdesk";

class HelpdeskDataService {
  create(payload: CreateHelpdeskTicket, idempotencyKey: string) {
    return baseHelpdeskApi.post<HelpdeskTicketDetails>("/tickets", payload, {
      headers: { "Idempotency-Key": idempotencyKey },
    });
  }

  uploadAttachment(ticketId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return baseHelpdeskApi.post(`/tickets/${ticketId}/attachments`, form);
  }

  getTicket(ticketId: string) {
    return baseHelpdeskApi.get<HelpdeskTicketDetails>(`/tickets/${ticketId}`);
  }

  getMine(params: {
    page?: number;
    size?: number;
    statuses?: TicketStatus[];
  } = {}) {
    return baseHelpdeskApi.get<HelpdeskPage<HelpdeskTicketSummary>>(
      "/tickets/my",
      { params: this.normalizeStatusParams(params) }
    );
  }

  getAll(params: {
    page?: number;
    size?: number;
    status?: TicketStatus | "";
    statuses?: TicketStatus[];
    category?: TicketCategory | "";
    priority?: TicketPriority | "";
    query?: string;
  }) {
    return baseHelpdeskApi.get<HelpdeskPage<HelpdeskTicketSummary>>(
      "/admin/tickets",
      { params: this.normalizeStatusParams(params) }
    );
  }

  getNewUnassignedCount() {
    return baseHelpdeskApi.get<number>("/admin/tickets/new-unassigned-count", {
      silent: true,
    } as any);
  }

  addMessage(ticketId: string, body: string, waitingForUser?: boolean) {
    return baseHelpdeskApi.post<HelpdeskMessage>(`/tickets/${ticketId}/messages`, {
      body,
      ...(typeof waitingForUser === "boolean" ? { waitingForUser } : {}),
    });
  }

  uploadMessageAttachment(ticketId: string, messageId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return baseHelpdeskApi.post<HelpdeskAttachment>(
      `/tickets/${ticketId}/messages/${messageId}/attachments`,
      form
    );
  }

  update(
    ticketId: string,
    payload: { status?: TicketStatus; priority?: TicketPriority }
  ) {
    return baseHelpdeskApi.patch<HelpdeskTicketDetails>(
      `/admin/tickets/${ticketId}`,
      payload
    );
  }

  assignToMe(ticketId: string) {
    return baseHelpdeskApi.patch<HelpdeskTicketDetails>(
      `/admin/tickets/${ticketId}/assign-to-me`
    );
  }

  downloadAttachment(ticketId: string, attachmentId: string) {
    return baseHelpdeskApi.get(
      `/tickets/${ticketId}/attachments/${attachmentId}`,
      { responseType: "blob" }
    );
  }

  getAssistantAvatar() {
    return baseHelpdeskApi.get<Blob>("/assistant/avatar", {
      responseType: "blob",
      silent: true,
    } as any);
  }

  updateAssistantAvatar(file: File) {
    const form = new FormData();
    form.append("file", file);
    return baseHelpdeskApi.put("/admin/assistant/avatar", form);
  }

  deleteAssistantAvatar() {
    return baseHelpdeskApi.delete("/admin/assistant/avatar");
  }

  private normalizeStatusParams<T extends { statuses?: TicketStatus[] }>(
    params: T
  ): Omit<T, "statuses"> & { statuses?: string } {
    const { statuses, ...rest } = params;
    return {
      ...rest,
      ...(statuses?.length ? { statuses: statuses.join(",") } : {}),
    };
  }
}

export default new HelpdeskDataService();
